import React, { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import BuilderModal from "../../Layouts/Modal/BuilderModal";
import Navbar from "../../Layouts/Navbar/Navbar";
import fileFolderConfig from "../../commonFunctions/fileFolderConfig";
import LeftSideBar, { FolderCollapse } from "../../components/SideBar/LeftSideBar";
import axiosInstance, { handleApiError } from "../../services/axiosService";
import { toastSuccess } from "../../utils/toastUtils";
import { useModalContext } from "../../context/ModalContext";
import { useTreeContext } from "../../context/TreeDataContext";
import { useWorkflowContext } from "../../context/WorkflowContext";
import { useNavigationContext } from "../../context/NavigationContext";
import { TryAgainMessage } from "../Screen/ScreenSection";
import SingleSelectBox from "../../components/FormComponents/SingleSelectBox";
import Button from "../../components/FormComponents/Button";
import ResizableComponent from "../../Layouts/ResizableComponent";
import HeaderModes from "../../Layouts/Navbar/HeaderModes";
import { deleteFolderOfFile, editFolderOrFile, insertNewFileOrFolder } from "../../utils/treeData/folderEditorUtils";

const WorkflowSection = () => {
  const { workflowExplorerData, setWorkflowExplorerData } = useTreeContext();
  const { selectedWorkflow, setSelectedWorkflow, fetchAllWorkflows } = useWorkflowContext();
  const [isLoader, setIsLoader] = useState(false);
  const { getUserConfirmationForDelete } = useModalContext();
  const [isError, setIsError] = useState(false);

  const [templateData, setTemplateData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { saveChangesAndNavigate } = useNavigationContext();
  const navigate = useNavigate();

  // const fetchAllWorkflows = async () => {
  //   try {
  //     setIsLoader(true);
  //     const { data } = await axiosInstance.get("/workflow");
  //     const treeData = getTreeDataForWorkflow(data);
  //     setWorkflowExplorerData(treeData);
  //   } catch (error) {
  //     handleError(error);
  //   } finally {
  //     setIsLoader(false);
  //   }
  // };

  let defaultDiagram = `
  <?xml version="1.0" encoding="UTF-8"?>
    <bpmn:definitions
      xmlns:bpmn="http://www.omg.org/spec/BPMN/20100524/MODEL"
      xmlns:cmmn="http://www.omg.org/spec/CMMN/20151109/MODEL"
      xmlns:bpmndi="http://www.omg.org/spec/BPMN/20100524/DI"
      xmlns:modeler="http://camunda.org/schema/modeler/1.0"
      id="Definitions_0bfwl6a" targetNamespace="http://bpmn.io/schema/bpmn"
      exporter="Camunda Modeler" exporterVersion="4.12.0"
      modeler:executionPlatform="Camunda Platform"
      modeler:executionPlatformVersion="7.15.0">
        <bpmn:process id="Process_0w0hjid" isExecutable="true" />
        <bpmndi:BPMNDiagram id="BPMNDiagram_1">
          <bpmndi:BPMNPlane id="BPMNPlane_1" bpmnElement="Process_0w0hjid" />
        </bpmndi:BPMNDiagram>
    </bpmn:definitions>
  `;

  const handleNewInsert = async (folderId, name) => {
    try {
      const config = {
        activities: [],
        name: name,
        xml_file: defaultDiagram
      };
      const { data, message } = await axiosInstance.post(`/workflow`, config);
      const newItem = fileFolderConfig.getFileConfig(data.id, data.name, folderId.name); // new item alwas delete and editable

      const newTree = insertNewFileOrFolder(workflowExplorerData, folderId, newItem);
      setWorkflowExplorerData(newTree);
      navigate(`./${data.id}`);
      toastSuccess(message);
    } catch (error) {
      handleApiError(error);
    }
  };

  const updateImportWorkflow = async (id) => {
    try {
      const { data } = await axiosInstance.post(`/workflow/templates/import/${id}`);

      const newItem = fileFolderConfig.getFileConfig(data.id, data.name, data.name); // new item alwas delete and editable

      const newTree = insertNewFileOrFolder(workflowExplorerData, { id: "1" }, newItem);
      setWorkflowExplorerData(newTree);
      setIsModalOpen(false);
      navigate(`./${data.id}`);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleDelete = async (folder) => {
    try {
      const userConfirmed = await getUserConfirmationForDelete(folder.name);
      if (!userConfirmed) return;

      const { message } = await axiosInstance.delete(`/workflow/${folder.id}`);
      const newTree = deleteFolderOfFile(workflowExplorerData, folder);
      setWorkflowExplorerData(newTree);
      if (folder.id === selectedWorkflow?.id) navigate("./");

      toastSuccess(message);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleEdit = async (folder, newName) => {
    try {
      const config = {
        name: newName
      };
      const { message } = await axiosInstance.put(`/workflow/${folder.id}`, config);
      const newTree = editFolderOrFile(workflowExplorerData, folder, newName);
      if (folder.id === selectedWorkflow?.id) setSelectedWorkflow({ ...selectedWorkflow, name: newName });
      setWorkflowExplorerData(newTree);
      toastSuccess(message);
    } catch (error) {
      handleApiError(error);
    }
  };

  const callbackFunction = (id) => {
    if (selectedWorkflow.id === id) return;
    // navigate(`./${id}`);
    saveChangesAndNavigate(id);
  };

  const importTemp = async () => {
    try {
      setIsLoader(true);
      setIsModalOpen(true);
      const { data } = await axiosInstance.get(`/workflow/templates`);
      setTemplateData(data);
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoader(false);
    }
  };

  const initialFetching = async () => {
    setIsError(false);
    try {
      if (workflowExplorerData && workflowExplorerData.length > 0) {
        setWorkflowExplorerData(workflowExplorerData);
      } else {
        fetchAllWorkflows();
      }
    } catch (error) {
      setIsError(true);
    }
  };

  useEffect(() => {
    initialFetching();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <section className="main_section">
        <div className="sub_section">
          {isError ? (
            <TryAgainMessage retryFunction={initialFetching} />
          ) : (
            <>
              <ResizableComponent
                resizeSide="right"
                storageKey="left_sidebar_size"
                defaultSize={244}
                range={[244, 400]} // Specify the range as [minSize, maxSize]
                collapsible={true}
              >
                <HeaderModes />
                <div className="d_flex">
                  <Navbar />
                  <LeftSideBar
                    explorerData={workflowExplorerData}
                    setExplorerData={setWorkflowExplorerData}
                    selectedScreen={selectedWorkflow}
                    callbackFunction={callbackFunction}
                    handleNewInsert={handleNewInsert}
                    handleDelete={handleDelete}
                    handleEdit={handleEdit}
                    type="Workflow"
                    isLoader={isLoader}
                    importTemp={importTemp}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                      <FolderCollapse
                        explorerData={workflowExplorerData}
                        setExplorerData={setWorkflowExplorerData}
                        type="workflow"
                      />
                    </div>
                  </LeftSideBar>
                </div>
              </ResizableComponent>
              <Outlet />
            </>
          )}
        </div>
      </section>

      <BuilderModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} modalName="Select Templates">
        <SelectTemplates
          templateData={templateData}
          setIsModalOpen={setIsModalOpen}
          updateImportWorkflow={updateImportWorkflow}
        />
      </BuilderModal>
    </>
  );
};

const SelectTemplates = ({ templateData, updateImportWorkflow }) => {
  const [selectedTemplate, setSelectedTemplate] = useState("");

  const handleImportClick = async () => {
    updateImportWorkflow(selectedTemplate);
  };

  return (
    <div>
      <div className="builder_form">
        <SingleSelectBox
          selectedData={selectedTemplate}
          options={templateData}
          onChange={(obj) => setSelectedTemplate(obj.id)}
          label={"Select a template"}
          placeholder={"Select a template"}
        />
      </div>
      <div>
        <Button onClick={handleImportClick}>Import</Button>
      </div>
    </div>
  );
};

export default WorkflowSection;
